import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [usuario, setUsuario] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  // Cargar perfil del usuario al iniciar
  useEffect(() => {
    const cargarPerfil = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const perfil = await authService.getPerfil();
          setUsuario(perfil);
        } catch (err) {
          // Token inválido, limpiar
          authService.logout();
        }
      }
      setCargando(false);
    };

    cargarPerfil();
  }, []);

  // Iniciar sesión
  const login = async (username, password) => {
    try {
      setError(null);
      const data = await authService.login(username, password);
      localStorage.setItem('access_token', data.access);
      localStorage.setItem('refresh_token', data.refresh);

      // Cargar perfil después del login
      const perfil = await authService.getPerfil();
      setUsuario(perfil);
      return perfil;
    } catch (err) {
      const errorData = err.response?.data;
      let mensaje = 'Error al iniciar sesión';
      if (errorData) {
        if (typeof errorData === 'string') {
          mensaje = errorData;
        } else if (errorData.detail) {
          mensaje = errorData.detail;
        } else if (errorData.non_field_errors && errorData.non_field_errors.length > 0) {
          mensaje = errorData.non_field_errors[0];
        } else {
          const keys = Object.keys(errorData);
          if (keys.length > 0) {
            const firstVal = errorData[keys[0]];
            mensaje = Array.isArray(firstVal) ? firstVal[0] : String(firstVal);
          }
        }
      }
      setError(mensaje);
      throw new Error(mensaje);
    }
  };

  // Cerrar sesión
  const logout = () => {
    authService.logout();
    setUsuario(null);
  };

  // Verificar si está autenticado
  const isAuthenticated = () => {
    return !!localStorage.getItem('access_token');
  };

  // ─── Verificar roles ───────────────────────────────────────────────────

  // Presidencia (super admin)
  const esPresidencia = () => usuario?.role === 'presidente' || usuario?.es_presidencia === true;
  const esPresidente = () => esPresidencia(); // alias backward compat

  // Administración
  const esAdministracion = () => usuario?.es_administracion === true || usuario?.role === 'administracion';
  const esAnalistaAdministracion = () => usuario?.es_analista_administracion === true || usuario?.role === 'analista_administracion';

  // VP genérico
  const esVicepresidencia = () => usuario?.es_vicepresidencia === true || usuario?.role === 'vicepresidencia';
  const esAsistenteVicepresidencia = () => usuario?.es_asistente_vicepresidencia === true || usuario?.role === 'asistente_vicepresidencia';
  const esAnalistaVp = () => usuario?.es_analista_vp === true || usuario?.role === 'analista';

  // Combinados
  const esVp = () => esVicepresidencia() || esAsistenteVicepresidencia();
  const esAsistenteVp = () => esAsistenteVicepresidencia();
  const esAnalista = () => usuario?.role === 'analista' || usuario?.role === 'analista_administracion';

  // ─── Helpers de visibilidad de estados de flujo ──────────────────────────
  /**
   * Retorna los estados que este usuario puede ver según su rol.
   * null = todos los estados.
   */
  const estadosPermitidos = () => {
    if (!usuario) return [];

    if (esPresidencia()) return null; // null = todos

    if (esAdministracion() || esAnalistaAdministracion()) return [
      'aprobado_vicepresidencia',
      'recibido_administracion', 'aprobado_administracion', 'rechazado_administracion',
      'pagado_parcial', 'pagado',
    ];

    if (esVicepresidencia() || esAsistenteVicepresidencia()) return [
      'elaborado', 'por_revision',
      'aprobado_vicepresidencia', 'rechazado_vicepresidencia',
    ];

    // Analistas: solo su propia bandeja
    return ['elaborado'];
  };

  /**
   * Retorna las transiciones de estado que puede hacer este usuario en un req dado.
   */
  const transicionesDisponibles = (requerimiento) => {
    if (!usuario || !requerimiento) return [];
    const estadoActual = requerimiento.estado_actual;

    if (esPresidencia()) {
      return ['por_revision', 'aprobado_vicepresidencia', 'rechazado_vicepresidencia',
        'recibido_administracion', 'aprobado_administracion', 'rechazado_administracion',
        'pagado_parcial', 'pagado'];
    }

    if (esAdministracion() || esAnalistaAdministracion()) {
      if (estadoActual === 'aprobado_vicepresidencia') return ['recibido_administracion'];
      if (estadoActual === 'recibido_administracion') return ['aprobado_administracion', 'rechazado_administracion'];
      if (estadoActual === 'aprobado_administracion') return ['pagado_parcial', 'pagado'];
      if (estadoActual === 'pagado_parcial') return ['pagado'];
      return [];
    }

    if (esVicepresidencia() || esAsistenteVicepresidencia()) {
      if (estadoActual === 'elaborado') return ['por_revision'];
      if (estadoActual === 'por_revision') return ['aprobado_vicepresidencia', 'rechazado_vicepresidencia'];
      return [];
    }

    return [];
  };

  const valor = {
    usuario,
    cargando,
    error,
    login,
    logout,
    isAuthenticated,
    // Roles
    esPresidencia,
    esPresidente,
    esAdministracion,
    esAnalistaAdministracion,
    esVicepresidencia,
    esAsistenteVicepresidencia,
    esAnalistaVp,
    esVp,
    esAsistenteVp,
    esAnalista,
    // Helpers
    estadosPermitidos,
    transicionesDisponibles,
  };

  return <AuthContext.Provider value={valor}>{children}</AuthContext.Provider>;
};

